import clsx from 'clsx'
import { pipe } from 'fp-ts/function'
import { getOrElse } from 'fp-ts/Option'
import { useEffect, useRef, useState } from 'react'

import { useCommonServices } from '@lib/hooks'

import { type BaseButtonProps } from '@components/ui-core/button/button'
import { Column } from '@components/ui-core/column/column'
import { Text } from '@components/ui-core/text/text'

import { CtaButton } from '../cta-button/cta-button'
import { monthlyPrices } from '../plan/utils'
import { PlanKinds, type PlansPageData, type PlanKind, type PlansPageFlag } from '../types'
import { isCtaDisabled } from '../utils'

import styles from './plans-comparison-floating-header.module.scss'

interface PlansComparisonFloatingHeaderProps {
  readonly data: Pick<PlansPageData['plansComparisonSection'], 'plans'>
  readonly highlighted: PlanKind
  readonly plansPageFlags: Set<PlansPageFlag>
  readonly featuresRef: React.RefObject<HTMLElement | null>
}

export const PlansComparisonFloatingHeader = ({
  data,
  highlighted,
  plansPageFlags,
  featuresRef,
}: PlansComparisonFloatingHeaderProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const floatingHeaderRef = useRef<HTMLDivElement>(null)

  const { shWebServerService } = useCommonServices()

  const plansData = shWebServerService.get().extras?.plansData

  const isPlanCtaDisabled = isCtaDisabled(plansPageFlags)

  useEffect(() => {
    const handleScroll = () => {
      if (!featuresRef.current) return

      const featuresElement = featuresRef.current
      const featuresRect = featuresElement.getBoundingClientRect()
      const shouldShow = featuresRect.top <= -100 && featuresRect.bottom > 250

      setIsVisible(shouldShow)
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [featuresRef])

  return (
    <div
      ref={floatingHeaderRef}
      className={clsx(styles.floatingHeader, isVisible && styles.visible)}
      aria-hidden={!isVisible}
    >
      <Column mainAxisAlignment="start" crossAxisAlignment="center">
        <table className={styles.headerTable}>
          <thead>
            <tr>
              <th className={clsx(styles.cell, styles.featureName)}></th>
              {PlanKinds.map((planKind: PlanKind) => {
                const {
                  billing: { price, cadence },
                  cta: { floatingLabel, url, disabledLabel },
                  title,
                } = data.plans[planKind]
                const planHighlighted = planKind === highlighted
                const category: BaseButtonProps['category'] = planHighlighted
                  ? 'primary'
                  : 'secondary'

                const disabled = isPlanCtaDisabled(planKind)
                const finalLabel = disabled ? disabledLabel : floatingLabel

                const { whenBilledAnnually: priceWhenBilledAnnually } = pipe(
                  monthlyPrices(planKind, plansData),
                  getOrElse(() => ({
                    whenBilledAnnually: price,
                  })),
                )

                return (
                  <th
                    key={planKind}
                    className={clsx(styles.cell, planHighlighted && styles.highlighted)}
                  >
                    <Column gap="2x">
                      <Text
                        as="span"
                        variant="bodyLarge"
                        fontWeight="bold"
                        className={styles.planName}
                      >
                        {title}
                      </Text>
                      <Column>
                        <Text as="span" variant="bodyMedium" className={styles.price}>
                          {priceWhenBilledAnnually}
                        </Text>
                        <Text as="span" variant="bodySmall" className={styles.cadence}>
                          {cadence}
                        </Text>
                      </Column>
                      <CtaButton
                        placement="FloatingHeader"
                        category={category}
                        disabled={disabled}
                        className={styles.cta}
                        url={url}
                        label={finalLabel}
                      />
                    </Column>
                  </th>
                )
              })}
            </tr>
          </thead>
        </table>
      </Column>
    </div>
  )
}
